import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiFehler, abmeldungBehandeln, token, tokenSetzen } from './api';
import { profilSetzen, type Profil } from './daten';
import { istNativ } from './plattform';
import {
  SPERRE_NACH_MS, biometrieAn, geraeteAngaben, identitaetPruefen,
  merkmalHolen, merkmalLoeschen, merkmalSpeichern, merkmalVorhanden,
} from '../../bereiche/anmeldung/lib/geraet';
// Desktop-App und installierte Web-App: eigene Ablage, eigene Regeln (90 Tage).
import * as desktop from '../../bereiche/anmeldung/lib/desktop';
import type { AboStand, Benutzer, Flags, Mandant } from './typen';

type AuthZustand = {
  benutzer: Benutzer | null;
  mandant: Mandant | null;
  flags: Flags | null;
  /** Land, Währung und Steuersätze des Betriebs. */
  profil: Profil | null;
  laedt: boolean;
  /** Grund, warum die letzte Sitzung endete — wird auf der Anmeldeseite gezeigt. */
  abmeldegrund: string | null;
  /** Konto traegt noch ein uebergebenes Passwort — bis zum Wechsel ist die App gesperrt. */
  passwortWechselErforderlich: boolean;
  /** Stand der E-Mail-Bestätigung dieses Kontos. */
  mailBestaetigung: MailBestaetigung | null;
  /** Stand des SEPA-Mandats dieses Betriebs. */
  mandat: MandatStand | null;
  /** Gratismonat, Kündigung, Vertragsende. */
  abo: AboStand | null;
  /** Nur in der App: ist dieses Gerät gemerkt? */
  geraetGemerkt: boolean;
  /** Nur in der App: Sperre nach Pause im Hintergrund. */
  gesperrt: boolean;
  /** Fragt nach Fingerabdruck oder Gesicht und hebt die Sperre auf. */
  entsperren: () => Promise<boolean>;
  /** Dieses Gerät vergessen — danach wieder Anmeldung mit Passwort. */
  geraetVergessen: () => Promise<void>;
  /**
   * Meldet an. Ist die Zwei-Faktor-Anmeldung aktiv, kommt statt einer Sitzung
   * ein Vorgang zurück — dann muss codePruefen() folgen.
   */
  anmelden: (kennung: string, passwort: string) => Promise<ZweiterFaktor | null>;
  /** Zweiter Schritt: Code aus der Mail einlösen. */
  codePruefen: (vorgangId: string, code: string) => Promise<void>;
  abmelden: () => void;
  aktualisieren: () => Promise<void>;
  istOwner: boolean;
  istAdmin: boolean;
};

export type MailBestaetigung = { noetig: boolean; bestaetigt: boolean; an: string | null };

export type MandatStand = {
  noetig: boolean;
  hatMandat: boolean;
  fristBis: string | null;
  tageRest: number | null;
  gesperrt: boolean;
  befreit: boolean;
};

export type ZweiterFaktor = {
  vorgangId: string;
  /** 'app' = Code aus der Authenticator-App (oder ein Ersatzcode), 'mail' = Code per E-Mail. */
  methode: 'mail' | 'app';
  mailMaske: string;
  gueltigMinuten: number;
};

/**
 * Fehler, nach denen sich ein erneuter Versuch lohnt: kein Netz (Status 0),
 * eine Ratenbremse oder ein Serverfehler. Das Token ist dabei in Ordnung.
 */
function voruebergehend(e: unknown): boolean {
  return e instanceof ApiFehler && (e.status === 0 || e.status === 429 || e.status >= 500);
}

/**
 * Die Sitzung beim Start wiederherstellen, mit drei Anläufen.
 *
 * Im Browser ist ein gescheitertes /auth/me selten. In der App ist es der
 * Alltag: beim Aufwecken steht das Funknetz noch nicht, und der erste Zugriff
 * läuft ins Leere. Früher flog der Benutzer dafür aus der Sitzung.
 */
async function mitAnlaeufen<T>(pfad: string): Promise<T> {
  let letzter: unknown;
  for (const warten of [0, 800, 2500]) {
    if (warten) await new Promise(r => setTimeout(r, warten));
    try { return await api<T>(pfad); } catch (e) {
      letzter = e;
      if (!voruebergehend(e)) break;
    }
  }
  throw letzter;
}

const Ctx = createContext<AuthZustand | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [benutzer, setBenutzer] = useState<Benutzer | null>(null);
  const [mandant, setMandant] = useState<Mandant | null>(null);
  const [flags, setFlags] = useState<Flags | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [abmeldegrund, setAbmeldegrund] = useState<string | null>(null);
  const [wechselNoetig, setWechselNoetig] = useState(false);
  const [mailBestaetigung, setMailBestaetigung] = useState<MailBestaetigung | null>(null);
  const [mandat, setMandat] = useState<MandatStand | null>(null);
  const [abo, setAbo] = useState<AboStand | null>(null);
  const [geraetGemerkt, setGeraetGemerkt] = useState(false);
  const [gesperrt, setGesperrt] = useState(false);
  const [profil, setProfil] = useState<Profil | null>(null);
  // Auf dem Rechner wird beim Start erst das gemerkte Gerät versucht. Solange
  // das läuft, gilt die Anwendung als „lädt" — sonst blitzt die Anmeldeseite auf.
  const [desktopPrueft, setDesktopPrueft] = useState(() => desktop.merktAnmeldung() && desktop.merkmalVermutlich());

  const zuruecksetzen = useCallback(() => {
    tokenSetzen(null);
    setBenutzer(null);
    setMandant(null);
    setFlags(null);
    setWechselNoetig(false);
    setMailBestaetigung(null);
    setMandat(null);
  }, []);

  const abmelden = useCallback(() => {
    // Diese Anmeldung auf dem Server beenden (und ihr Gerät mit) — Anwesenheit
    // springt dabei sofort auf offline. Fehler hier sind belanglos.
    api('/auth/abmelden', { methode: 'POST' }).catch(() => {});
    // In der App auch das gemerkte Gerät auflösen: wer sich abmeldet, will
    // nicht, dass der nächste Start ihn wieder hineinlässt.
    if (istNativ()) {
      void merkmalLoeschen();
      setGeraetGemerkt(false);
      setGesperrt(false);
    }
    if (desktop.merktAnmeldung()) void desktop.merkmalLoeschen();
    zuruecksetzen();
    setAbmeldegrund(null);
  }, [zuruecksetzen]);

  // Wird der Zugang während der Nutzung eingefroren, meldet die API-Schicht das
  // hier — der Benutzer fliegt raus und erfährt den Grund.
  useEffect(() => {
    abmeldungBehandeln(({ nachricht }) => {
      zuruecksetzen();
      setAbmeldegrund(nachricht);
      // Auf dem Rechner: erst still über das gemerkte Gerät versuchen. Ist nur
      // die 24-Stunden-Sitzung abgelaufen, geht es ohne Anmeldung weiter; ist
      // das Gerät abgemeldet, lehnt der Server ab und die Anmeldung erscheint.
      if (desktop.merktAnmeldung() && desktop.merkmalVermutlich()) setDesktopPrueft(true);
    });
  }, [zuruecksetzen]);

  const laden = useCallback(async () => {
    if (!token()) { setLaedt(false); return; }
    try {
      const ich = await mitAnlaeufen<{
        user: Benutzer; tenant: Mandant; passwortWechselErforderlich?: boolean;
        mailBestaetigung?: MailBestaetigung; mandat?: MandatStand; abo?: AboStand | null;
      }>('/auth/me');
      setAbmeldegrund(null);
      setBenutzer(ich.user);
      setMandant(ich.tenant);
      setWechselNoetig(!!ich.passwortWechselErforderlich);
      setMailBestaetigung(ich.mailBestaetigung ?? null);
      setMandat(ich.mandat ?? null);
      setAbo(ich.abo ?? null);
      // Nach Vertragsende gibt es nur noch die Datenkopie — keine Funktionen mehr zu laden.
      if (ich.abo?.phase === 'beendet') return;
      const f = await api<{ plan: string; flags: Flags }>('/billing/features');
      setFlags(f.flags);
      // Land, Währung und Steuersätze des Betriebs — sie bestimmen, wie Beträge
      // angezeigt und welche Steuersätze angeboten werden.
      try {
        const firma = await api<{ laenderprofil: Profil }>('/firma');
        profilSetzen(firma.laenderprofil);
        setProfil(firma.laenderprofil);
      } catch { /* Standardprofil bleibt bestehen */ }
    } catch (e) {
      if (e instanceof ApiFehler && e.istGesperrt) {
        // Die Sperre meldet api() bereits selbst — hier nichts weiter tun.
      } else if (voruebergehend(e)) {
        // Zugang in Ordnung, nur die Leitung nicht: das Token bleibt liegen.
        // Beim nächsten Start ist der Benutzer wieder drin, ohne sein
        // Passwort noch einmal zu tippen.
        setAbmeldegrund('Keine Verbindung zum Server. Bitte erneut versuchen.');
      } else {
        zuruecksetzen();
      }
    } finally {
      setLaedt(false);
    }
  }, [zuruecksetzen]);

  useEffect(() => { void laden(); }, [laden]);

  /**
   * `ueberGeraet`: die Sitzung kam über ein schon gemerktes Gerät. Dann wird
   * KEIN neues Gerät angemeldet — vorher legte jeder Fingerabdruck-Start ein
   * weiteres an, bis die Liste voll war; auf dem Rechner hätte jeder Start die
   * 90 Tage neu beginnen lassen.
   */
  const sitzungUebernehmen = useCallback(async (antwort: {
    token: string; user: Benutzer; tenant: Mandant; passwortWechselErforderlich?: boolean;
  }, { ueberGeraet = false }: { ueberGeraet?: boolean } = {}) => {
    tokenSetzen(antwort.token);
    setBenutzer(antwort.user);
    setMandant(antwort.tenant);
    setAbmeldegrund(null);
    setWechselNoetig(!!antwort.passwortWechselErforderlich);
    try {
      const f = await api<{ plan: string; flags: Flags }>('/billing/features');
      setFlags(f.flags);
    } catch { /* Tarif-Flags sind nicht kritisch für die Anmeldung */ }
    try {
      const firma = await api<{ laenderprofil: Profil }>('/firma');
      profilSetzen(firma.laenderprofil);
      setProfil(firma.laenderprofil);
    } catch { /* Standardprofil bleibt bestehen */ }
    // Bestätigungs- und Mandatsstand stehen nicht in der Anmeldeantwort, sie
    // kommen aus /auth/me — ohne diesen Zug griffe die Sperre erst beim
    // nächsten Laden der Seite.
    try {
      const ich = await api<{ mailBestaetigung?: MailBestaetigung; mandat?: MandatStand; abo?: AboStand | null }>('/auth/me');
      setMailBestaetigung(ich.mailBestaetigung ?? null);
      setMandat(ich.mandat ?? null);
      setAbo(ich.abo ?? null);
    } catch { /* beim nächsten Laden */ }

    // In der App dieses Gerät merken, damit beim nächsten Start der
    // Fingerabdruck genügt. Im Browser passiert hier nichts.
    if (istNativ() && !ueberGeraet && !antwort.passwortWechselErforderlich) {
      try {
        const angaben = await geraeteAngaben();
        const d = await api<{ merkmal: string }>('/auth/geraet', { methode: 'POST', daten: angaben });
        setGeraetGemerkt(await merkmalSpeichern(d.merkmal));
      } catch {
        // Scheitert das Merken, bleibt die Anmeldung trotzdem gültig — der
        // Benutzer tippt beim nächsten Start eben wieder sein Passwort.
      }
    }
    // Auf dem Rechner (Desktop-App, installierte Web-App) genauso — der
    // Server lässt dieses Gerät nach 90 Tagen ablaufen.
    if (desktop.merktAnmeldung() && !ueberGeraet && !antwort.passwortWechselErforderlich) {
      try {
        const d = await api<{ merkmal: string }>('/auth/geraet', { methode: 'POST', daten: desktop.geraeteAngaben() });
        await desktop.merkmalSpeichern(d.merkmal);
      } catch { /* dann eben beim nächsten Start wieder das Passwort */ }
    }
  }, []);

  // --- Desktop-App und installierte Web-App ---------------------------------
  // Beim Start ohne gültige Sitzung: mit dem gemerkten Gerät anmelden. Nach 90
  // Tagen (oder Abmelden aus der Ferne, Passwortwechsel) lehnt der Server ab —
  // dann verschwindet das Merkmal und die Anmeldeseite erscheint.
  useEffect(() => {
    if (!desktopPrueft || laedt) return;
    if (benutzer) { setDesktopPrueft(false); return; }
    void (async () => {
      const merkmal = await desktop.merkmalLesen();
      if (merkmal) {
        try {
          const antwort = await api<{
            token: string; user: Benutzer; tenant: Mandant; passwortWechselErforderlich?: boolean;
          }>('/auth/geraet/anmelden', { methode: 'POST', daten: { merkmal } });
          await sitzungUebernehmen(antwort, { ueberGeraet: true });
        } catch (e) {
          if (e instanceof ApiFehler && (e.code === 'GERAET_UNGUELTIG' || e.status === 401)) await desktop.merkmalLoeschen();
        }
      }
      setDesktopPrueft(false);
    })();
  }, [desktopPrueft, laedt, benutzer, sitzungUebernehmen]);

  // --- Nur in der App -------------------------------------------------------

  /**
   * Beim Start: liegt ein gemerktes Gerät vor, wird nach Fingerabdruck oder
   * Gesicht gefragt und damit eine frische Sitzung geholt. Erst wenn das
   * scheitert, erscheint die Anmeldeseite.
   */
  const geraetAnmeldung = useCallback(async () => {
    if (!istNativ()) return false;
    if (!(await merkmalVorhanden())) return false;
    setGeraetGemerkt(true);

    const geholt = await merkmalHolen('Anmeldung bei LagerPro');
    if (!geholt.ok) {
      // Abgebrochen: die Sperre bleibt stehen, der Benutzer kann es erneut
      // versuchen oder sich mit Passwort anmelden.
      if (geholt.grund === 'abgebrochen') setGesperrt(true);
      return false;
    }

    try {
      const antwort = await api<{
        token: string; user: Benutzer; tenant: Mandant; passwortWechselErforderlich?: boolean;
      }>('/auth/geraet/anmelden', { methode: 'POST', daten: { merkmal: geholt.merkmal } });
      await sitzungUebernehmen(antwort, { ueberGeraet: true });
      setGesperrt(false);
      return true;
    } catch (e) {
      // Aus der Ferne abgemeldet oder Passwort geändert: das Merkmal ist
      // wertlos und muss aus dem Schlüsselbund verschwinden, sonst fragt die
      // App bei jedem Start nach einem Fingerabdruck, der zu nichts führt.
      if (e instanceof ApiFehler && (e.code === 'GERAET_UNGUELTIG' || e.status === 401)) {
        await merkmalLoeschen();
        setGeraetGemerkt(false);
      }
      setGesperrt(false);
      return false;
    }
  }, [sitzungUebernehmen]);

  // Beim ersten Laden ohne gültige Sitzung: über das Gerät versuchen.
  useEffect(() => {
    if (!istNativ() || laedt || benutzer) return;
    void geraetAnmeldung();
    // Absichtlich nur von laedt und benutzer abhängig: der Versuch soll genau
    // einmal laufen, wenn feststeht, dass keine Sitzung da ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laedt, benutzer]);

  /**
   * Sperre nach einer Pause im Hintergrund.
   *
   * Fünf Minuten sind der Mittelweg: wer das Telefon kurz weglegt oder etwas
   * nachschlägt, kommt ohne neue Prüfung zurück. Wer es liegen lässt, nicht.
   */
  useEffect(() => {
    if (!istNativ()) return;
    let abmelden: (() => void) | undefined;
    let seit = 0;

    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const horcher = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) { seit = Date.now(); return; }
          if (!seit || !biometrieAn()) return;
          const pause = Date.now() - seit;
          seit = 0;
          if (pause > SPERRE_NACH_MS) setGesperrt(true);
        });
        abmelden = () => { void horcher.remove(); };
      } catch { /* im Browser nicht vorhanden */ }
    })();

    return () => abmelden?.();
  }, []);

  const entsperren = useCallback(async () => {
    const ok = await identitaetPruefen('LagerPro entsperren');
    if (ok) setGesperrt(false);
    return ok;
  }, []);

  const geraetVergessen = useCallback(async () => {
    await merkmalLoeschen();
    setGeraetGemerkt(false);
  }, []);

  /** Übernimmt eine fertige Anmeldeantwort in den Zustand. */
  const anmelden = useCallback(async (kennung: string, passwort: string) => {
    const antwort = await api<{
      token?: string; user?: Benutzer; tenant?: Mandant; passwortWechselErforderlich?: boolean;
      zweiterFaktor?: boolean; vorgangId?: string; mailMaske?: string; gueltigMinuten?: number; methode?: 'mail' | 'app';
    }>('/auth/login', {
      methode: 'POST',
      daten: { identifier: kennung, password: passwort },
    });

    // Zweiter Faktor: noch keine Sitzung, nur ein Vorgang.
    if (antwort.zweiterFaktor && antwort.vorgangId) {
      return {
        vorgangId: antwort.vorgangId,
        methode: antwort.methode === 'app' ? 'app' as const : 'mail' as const,
        mailMaske: antwort.mailMaske ?? '',
        gueltigMinuten: antwort.gueltigMinuten ?? 10,
      };
    }

    await sitzungUebernehmen(antwort as { token: string; user: Benutzer; tenant: Mandant });
    return null;
  }, [sitzungUebernehmen]);

  const codePruefen = useCallback(async (vorgangId: string, code: string) => {
    const antwort = await api<{ token: string; user: Benutzer; tenant: Mandant; passwortWechselErforderlich?: boolean }>(
      '/auth/2fa/pruefen', { methode: 'POST', daten: { vorgangId, code } });
    await sitzungUebernehmen(antwort);
  }, [sitzungUebernehmen]);

  const wert = useMemo<AuthZustand>(() => ({
    benutzer, mandant, flags, profil, laedt: laedt || desktopPrueft, abmeldegrund,
    passwortWechselErforderlich: wechselNoetig,
    mailBestaetigung, mandat, abo,
    geraetGemerkt, gesperrt, entsperren, geraetVergessen,
    anmelden, codePruefen, abmelden, aktualisieren: laden,
    istOwner: !!benutzer?.isOwner,
    istAdmin: benutzer?.role === 'admin' || !!benutzer?.isOwner,
  }), [benutzer, mandant, flags, profil, laedt, desktopPrueft, abmeldegrund, wechselNoetig,
    mailBestaetigung, mandat, abo,
    geraetGemerkt, gesperrt, entsperren, geraetVergessen, anmelden, codePruefen, abmelden, laden]);

  return <Ctx.Provider value={wert}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth benötigt einen AuthProvider');
  return c;
}
